import React, { useState } from 'react';
import { 
  Building2, 
  Menu, 
  X, 
  LogOut, 
  Settings, 
  User,
  LayoutDashboard,
  Target,
  CalendarDays,
  Users,
  Search,
  Database,
  Trophy,
  HelpCircle,
  Building,
  ArrowLeftRight,
  ClipboardList,
  ChevronRight
} from 'lucide-react';
import { useLocation, useNavigate, NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const MobileHeader = ({ onMenuClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, canSwitchPortal, switchPortal, activePortal } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  
  const isAgentPage = location.pathname.startsWith('/agent');
  const isAdminPage = location.pathname.startsWith('/admin');
  const isSuperAdminPage = location.pathname.startsWith('/super-admin');
  
  if (!isAgentPage && !isAdminPage && !isSuperAdminPage) return null;

  // Admin navigation links
  const adminLinks = [
    { to: '/admin', icon: LayoutDashboard, label: 'Tableau de bord', exact: true },
    { to: '/admin/opportunites', icon: Target, label: 'Opportunités' },
    { to: '/admin/agenda', icon: CalendarDays, label: 'Agenda' },
    { to: '/admin/agents', icon: Users, label: 'Agents' },
    { to: '/admin/registre', icon: Search, label: 'Recherche Copropriétés' },
    { to: '/admin/base-nationale', icon: Database, label: 'Base Nationale' },
    { to: '/admin/cee-settings', icon: Settings, label: 'Paramètres CEE' },
    { to: '/admin/gamification', icon: Trophy, label: 'Gamification' },
    { to: '/admin/support', icon: HelpCircle, label: 'Support' },
    { to: '/admin/mon-entreprise', icon: Building, label: 'Mon Entreprise' },
  ];

  // Agent navigation links
  const agentLinks = [
    { to: '/agent', icon: LayoutDashboard, label: 'Tableau de bord', exact: true },
    { to: '/agent/recherche', icon: Search, label: 'Recherche Copropriétés' },
    { to: '/agent/ma-prospection', icon: ClipboardList, label: 'Ma Prospection' },
    { to: '/agent/agenda', icon: CalendarDays, label: 'Agenda' },
  ];

  const links = isAdminPage ? adminLinks : agentLinks;

  // Get page title based on route
  const getPageTitle = () => {
    if (location.pathname === '/agent') return 'Tableau de bord';
    if (location.pathname === '/admin') return 'Dashboard Admin';
    if (location.pathname.includes('/recherche')) return 'Recherche';
    if (location.pathname.includes('/ma-prospection')) return 'Ma Prospection';
    if (location.pathname.includes('/prospection')) return 'Ma Prospection';
    if (location.pathname.includes('/profil')) return 'Mon Profil';
    if (location.pathname.includes('/mon-entreprise')) return 'Mon Entreprise';
    if (location.pathname.includes('/agents')) return 'Agents';
    if (location.pathname.includes('/agenda')) return 'Agenda';
    if (location.pathname.includes('/opportunites')) return 'Opportunités';
    if (location.pathname.includes('/base-nationale')) return 'Base Nationale';
    if (location.pathname.includes('/cee-settings')) return 'Paramètres CEE';
    if (location.pathname.includes('/gamification')) return 'Gamification';
    if (location.pathname.includes('/support')) return 'Support';
    return 'CoproEnergy';
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    setShowMenu(false);
  };

  const handleSwitchPortal = () => {
    switchPortal();
    setShowMenu(false);
  };

  const handleNavClick = (to) => {
    navigate(to);
    setShowMenu(false);
  };

  return (
    <>
      <header className="mobile-header" data-testid="mobile-header">
        <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
          <Building2 className="w-5 h-5 text-white" />
        </div>
        <h1 className="text-white font-semibold text-base truncate flex-1 mx-2">{getPageTitle()}</h1>
        <button
          onClick={() => setShowMenu(!showMenu)}
          className="p-2 text-white hover:bg-white/10 rounded-lg flex-shrink-0"
          data-testid="mobile-menu-btn"
        >
          {showMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </header>
      
      {/* Full Screen Mobile Menu */}
      {showMenu && (
        <div className="fixed inset-0 top-14 bg-slate-900 z-40 md:hidden overflow-y-auto">
          <div className="p-4">
            {/* User Info */}
            <div className="flex items-center gap-3 p-3 bg-slate-800 rounded-lg mb-4">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium flex-shrink-0">
                {user?.prenom?.[0]}{user?.nom?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">
                  {user?.prenom} {user?.nom}
                </p>
                <p className="text-sm text-slate-400 truncate">{user?.email}</p>
              </div>
            </div>

            {/* Portal Switch Button */}
            {canSwitchPortal && canSwitchPortal() && (
              <button
                onClick={handleSwitchPortal}
                className="w-full flex items-center gap-3 p-3 mb-4 bg-blue-600/20 text-blue-400 rounded-lg border border-blue-500/30"
                data-testid="mobile-switch-portal"
              >
                <ArrowLeftRight className="w-5 h-5" />
                <span className="flex-1 text-left">Basculer vers {activePortal === 'admin' ? 'Agent' : 'Admin'}</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            )}

            {/* Navigation Links */}
            <div className="space-y-1 mb-4">
              <p className="text-xs text-slate-500 uppercase tracking-wider px-3 mb-2">
                Navigation
              </p>
              {links.map((link) => {
                const isActive = link.exact 
                  ? location.pathname === link.to
                  : location.pathname.startsWith(link.to);
                
                return (
                  <button
                    key={link.to}
                    onClick={() => handleNavClick(link.to)}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
                      isActive 
                        ? 'bg-blue-600 text-white' 
                        : 'text-slate-300 hover:bg-slate-800'
                    }`}
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <link.icon className="w-5 h-5" />
                    <span>{link.label}</span>
                    {isActive && <ChevronRight className="w-5 h-5 ml-auto" />}
                  </button>
                );
              })}
            </div>

            {/* Account Section */}
            <div className="border-t border-slate-700 pt-4 space-y-1">
              <p className="text-xs text-slate-500 uppercase tracking-wider px-3 mb-2">
                Compte
              </p>
              <button
                onClick={() => handleNavClick(isAdminPage ? '/admin/profil' : '/agent/profil')}
                className="w-full flex items-center gap-3 px-3 py-3 text-slate-300 hover:bg-slate-800 rounded-lg"
              >
                <User className="w-5 h-5" />
                <span>Mon Profil</span>
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-3 text-red-400 hover:bg-red-500/10 rounded-lg"
                data-testid="mobile-logout-btn"
              >
                <LogOut className="w-5 h-5" />
                <span>Déconnexion</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileHeader;
